<template>
  <BaseImage
    id="clock"
    :src="clockImageSrc"
    alt="timer"
  />

  <div :class="['time_counter', {ending: isTimerEndingActive}]">
    {{ formattedTime }}
  </div>
</template>

<script setup>
import moment from 'moment';
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import BaseImage from '@/components/image/BaseImage.vue';
import { isPositiveNumber } from '@/helpers/common.js';
import { formatTime } from '@/helpers/time.js';

const TIMER_INTERVAL_DELAY = 1000;
const TIMER_ENDING_LIMIT_IN_SECONDS = 10;
const TIMER_TOTAL_IN_SECONDS = 60;

const emits = defineEmits(['time:expired']);

const props = defineProps({
  startImmediate: {
    type: Boolean,
    default: true,
  },
  initialValueInSeconds: {
    type: Number,
    default: 0,
  },
});

const timerInSeconds = ref(props.initialValueInSeconds);
const timerIntervalId = ref(null);

const formattedTime = computed(() => formatTime(timerInSeconds.value, '00:00'));
const isTimerEndingActive = computed(() => timerInSeconds.value <= TIMER_ENDING_LIMIT_IN_SECONDS);
const clockImageSrc = computed(() => isTimerEndingActive.value ? '/static/images/clock_red.svg' : '/static/images/clock_white.svg');

const intervalCleanup = () => {
  if (timerIntervalId.value) {
    clearInterval(timerIntervalId.value);
    timerIntervalId.value = null;
  }
};

const startTimer = () => {
  const end = moment(moment().add(props.initialValueInSeconds || TIMER_TOTAL_IN_SECONDS, 'seconds').toDate());

  const updateTimer = () => {
    const now = moment();

    const remainingTime = Math.ceil(moment.duration(end.diff(now)).asSeconds());

    if (isPositiveNumber(remainingTime)) {
      timerInSeconds.value = remainingTime;
    } else {
      intervalCleanup();
      timerInSeconds.value = 0;

      emits('time:expired');
    }
  };

  updateTimer();

  timerIntervalId.value = setInterval(updateTimer, TIMER_INTERVAL_DELAY);
};

const stopTimer = () => {
  intervalCleanup();
};

onMounted(() => {
  if (props.startImmediate) {
    nextTick(() => {
      startTimer();
    });
  }
});

onBeforeUnmount(intervalCleanup);

defineExpose({
  startTimer,
  stopTimer,
});
</script>
